import { FC, useCallback, useState } from 'react';
import { css } from '@emotion/react';
import TextareaAutosize from 'react-textarea-autosize';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { SendAction } from './send-action';

interface InputBoxProps {
  placeholder?: string;
  onSend?: (value: string) => void;
}

export const InputBox: FC<InputBoxProps> = ({ placeholder, onSend }) => {
  const [value, setValue] = useState('');
  const { t } = useTranslation('aiOnboarding');

  const handleSend = useCallback(() => {
    if (value.trim() && onSend) {
      const trimmedValue = value.replace(/^\n+|\n+$/g, ''); // Trim leading/trailing newlines
      onSend(trimmedValue);
      setValue('');
    }
  }, [value, onSend]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const isDisabled = !value.trim();

  return (
    <div css={styles.container}>
      <TextareaAutosize
        value={value}
        name='chat-input'
        placeholder={placeholder || t('Ask me anything...')}
        id='chat-input-textarea'
        onChange={(e) => setValue(e.target.value)}
        minRows={1}
        maxRows={3}
        spellCheck={false}
        onKeyUp={handleKeyPress}
        css={styles.input}
      />

      <SendAction onClick={handleSend} disabled={isDisabled} />
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    align-items: center;
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.large};
    padding: ${theme.spacing(1)};
    gap: ${theme.spacing(1)};
  `,
  input: css`
    border: none;
    outline: none;
    flex: 1;
    padding: ${theme.spacing(1)};
    background-color: transparent;
    resize: none;
  `,
};
